import { cx } from '@flexbyt/widget-preview';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/**
 * Article images, opened at the size they were captured.
 *
 * ===========================================================================
 * Why an article needs this at all
 * ===========================================================================
 * A body screenshot is a whole 1440px dashboard window, and it is drawn in a
 * reading column because that is where it belongs — an image wider than the
 * paragraph it illustrates stops being part of the same thought. The cost is
 * that the shot is honest about *where* a control is and unreadable about *what
 * it says*, which is the half a reader following a procedure needs. So every
 * figure is a button, and the full capture is one click away.
 *
 * ===========================================================================
 * One dialog for the article, not one per figure
 * ===========================================================================
 * The obvious build gives each figure its own `<dialog>`. A long guide then
 * carries a dozen modals, a dozen effects and a dozen copies of the same
 * markup, all so that at most one can ever be open. The provider owns a single
 * dialog and figures ask it to show them — the same reason a toast host exists
 * once per app rather than once per toast.
 *
 * `showModal()` rather than the `open` attribute, for the reasons spelled out
 * in `PreviewStage`: only the method call gives the top layer, the focus trap,
 * Escape, and `::backdrop`.
 */
export type Frame = {
  url: string;
  /** Doubles as the caption, as it does under the figure on the page. */
  alt: string;
  width?: number;
  height?: number;
};

const LightboxContext = createContext<((frame: Frame) => void) | null>(null);

/**
 * Null outside a provider, so a figure rendered somewhere without one — a
 * preview, a test — is a plain image rather than a crash.
 */
export function useLightbox() {
  return useContext(LightboxContext);
}

export function LightboxProvider({ children }: { children: ReactNode }) {
  const [frame, setFrame] = useState<Frame | null>(null);
  const open = useCallback((next: Frame) => setFrame(next), []);

  return (
    <LightboxContext.Provider value={open}>
      {children}
      <Lightbox frame={frame} onClose={() => setFrame(null)} />
    </LightboxContext.Provider>
  );
}

function Lightbox({ frame, onClose }: { frame: Frame | null; onClose: () => void }) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (frame && !dialog.open) dialog.showModal();
    if (!frame && dialog.open) dialog.close();
  }, [frame]);

  return (
    <dialog
      ref={dialogRef}
      aria-label="Image, full screen"
      onClose={onClose}
      onClick={(event) => {
        /*
          The backdrop is not a child, so a click that lands on the dialog
          element itself came from outside the panel. The image sits in a padded
          box, so the room around it closes too — which is what a reader who has
          finished looking will try first.
        */
        if (event.target === dialogRef.current) onClose();
      }}
      className={cx(
        'h-dvh max-h-dvh w-screen max-w-none border-0 bg-transparent p-0',
        'backdrop:bg-ink-950/80'
      )}
    >
      {frame ? (
        /*
          The panel paints its own ground rather than letting the `::backdrop`
          show the article through: a screenshot is mostly white, and a white
          image on a half-lit page has no edge — it reads as part of the page it
          is supposed to be floating above.
        */
        <div className="flex h-dvh flex-col bg-ink-950/95">
          <div className="flex shrink-0 items-center justify-end px-4 py-3 sm:px-6">
            <button
              type="button"
              onClick={onClose}
              autoFocus
              className={cx(
                'inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5',
                'text-body-sm font-medium text-white/80 ring-1 ring-white/25',
                'transition-colors duration-[var(--duration-fast)]',
                'hover:bg-white/10 hover:text-white'
              )}
            >
              Close
              <kbd className="rounded border border-white/25 px-1 text-micro font-normal text-white/70">
                Esc
              </kbd>
            </button>
          </div>

          {/*
            `object-contain` inside a flexible box, capped at the file's own
            width: a 1600px capture stretched across a 4K display is bigger and
            worse, and the point of this view is legibility.
          */}
          <div className="flex min-h-0 flex-1 items-center justify-center px-4 sm:px-6">
            <img
              src={frame.url}
              alt={frame.alt}
              width={frame.width}
              height={frame.height}
              className="max-h-full w-auto rounded-lg object-contain"
              style={{ maxWidth: frame.width ? `min(100%, ${frame.width}px)` : '100%' }}
            />
          </div>

          {frame.alt ? (
            <p className="mx-auto shrink-0 max-w-text px-4 py-4 text-center text-caption text-white/70 sm:px-6">
              {frame.alt}
            </p>
          ) : (
            <div className="h-4 shrink-0" />
          )}
        </div>
      ) : null}
    </dialog>
  );
}

export function ZoomIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      className="size-3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M7 11.5a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9ZM10.5 10.5 14 14M7 5v4M5 7h4" />
    </svg>
  );
}
